import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import mint from '@tunein/mint';
import { extractLotameInterestsFromGuideItem } from '../../utils/ads/lotameUtils';

export default function withLotameTargeting(BaseComponent) {
  class WithLotameTargeting extends Component {
    static propTypes = {
      match: PropTypes.object.isRequired,

      // mapStateToProps
      categories: PropTypes.object.isRequired,
      profiles: PropTypes.object.isRequired,
      isMintInitialized: PropTypes.bool.isRequired,
      lotameInterests: PropTypes.array.isRequired,
    };

    componentDidMount() {
      // On initial app load, Mint initializes (WithAds.js) after this HOC mounts, so signalPageChange() won't execute
      // here. Instead, Lotame interests are signaled via setInitialLotameInterests in componentDidUpdate(), after Mint
      // initializes. However, we do need to signal page changes on mount, occasionally. As this HOC is used on
      // different route connectors, this lifecycle method will execute when navigating to a new route with a different
      // route signature (e.g., from `/radio/:guideIdentifier` to `/podcasts/:podcastGuideIdentifier` or vanity URL)
      if (this.props.isMintInitialized) {
        this.signalPageChange(this.props);
      }
    }

    componentDidUpdate(prevProps) {
      // As mentioned in componentDidMount(), we are setting the initial Lotame interests here on initial app load,
      // after Mint initializes.
      if (!prevProps.isMintInitialized && this.props.isMintInitialized) {
        this.setInitialLotameInterests();
        return;
      }

      // When navigating to a new page with the same route signature (e.g., from `/radio/music` to
      // `/radio/Classic-Hits-s249942`), this HOC will not re-mount, so we need to instead signal a page change when the
      // guideIdentifier changes on the match.params object.
      const prevGuideIdentifier = get(
        prevProps,
        'match.params.guideIdentifier',
      );
      const nextGuideIdentifier = get(
        this.props,
        'match.params.guideIdentifier',
      );
      if (prevGuideIdentifier !== nextGuideIdentifier) {
        this.signalPageChange(this.props);
        return;
      }

      if (prevProps.lotameInterests !== this.props.lotameInterests) {
        this.updateLotameInterests(this.props.lotameInterests);
      }
    }

    setInitialLotameInterests() {
      const interests = this.getLotameInterests(this.props) || [];

      if (interests.length) {
        this.updateLotameInterests(interests);
      }
    }

    getLotameInterests(props) {
      const guideId = get(props, 'routeProps.guideContext.guideId', '');
      const guideItem = props.categories[guideId] || props.profiles[guideId];

      return extractLotameInterestsFromGuideItem(guideItem) || [];
    }

    updateLotameInterests(interests) {
      mint.addLotameInterests(interests);
    }

    signalPageChange(props) {
      mint.triggerLotameTargeting(this.getLotameInterests(props));
    }

    render() {
      return <BaseComponent {...this.props} />;
    }
  }

  function mapStateToProps({
    categories,
    profiles,
    mint: { isInitialized, lotameInterests },
  }) {
    return {
      categories,
      profiles,
      isMintInitialized: isInitialized,
      lotameInterests,
    };
  }

  return connect(mapStateToProps)(WithLotameTargeting);
}
